import { BaseComponent } from '../base-component';
import { session } from '../../client/session';
import { api } from '../../client/api';
import type { UserSession } from '../../shared/types';
import { toast } from '../../client/toast';
import { stripHtml } from '../../shared/utils';
import styles from './settings-panel.css?inline';

export class SettingsPanel extends BaseComponent {
  private user: UserSession | null = null;
  private saving = false;
  private changingPassword = false;
  private resendingEmail = false;
  private checkingSlug = false;
  private slugAvailable: boolean | null = null;
  private slugCheckTimeout: number | null = null;
  private showSlugConfirmModal = false;
  private pendingSlug = '';

  constructor() {
    super();
    this.user = session.getUser();
  }

  protected styles(): string {
    return styles;
  }

  protected template(): string {
    return this.renderContent();
  }

  private renderContent(): string {

    if (!this.user) {
      return `<div>Please log in to view settings.</div>`;
    }

    const isCompany = this.user.accountType === 'company';
    const emailVerified = this.user.emailVerified;

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>Settings</h2>
          <p class="description">You can update and manage your personal account settings here.</p>
        </ag-panel-header>
        <ag-panel-content>

          <form class="settings-form" id="profile-form">
            <div class="section-title">Profile Information</div>

            <ag-form-group>
              <label for="name">Name</label>
              <input class="form-control" type="text" id="name" name="name" value="${this.user.name || ''}" required />
            </ag-form-group>

            <ag-form-group>
              <label for="email">Email</label>
              <input class="form-control" type="email" id="email" value="${this.user.email}" disabled />
              ${!emailVerified ? `<small class="text-muted">Email not verified</small>` : ''}
            </ag-form-group>

            ${!emailVerified ? `
            <div class="ag-button-group right">
              <button type="button" class="btn btn-danger" id="resend-verification" ${this.resendingEmail ? 'disabled' : ''}>
                ${this.resendingEmail ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>
            ` : ''}

            ${isCompany ? `
            <ag-form-group>
              <label for="companyName">Company Name</label>
              <input class="form-control" type="text" id="companyName" name="companyName" value="${this.user.companyName || ''}" />
            </ag-form-group>
            ` : ''}

            <ag-form-group>
              <label>Account Type</label>
              <input class="form-control" type="text" value="${isCompany ? 'Company' : 'Personal'}" disabled />
            </ag-form-group>

            <div class="ag-button-group right">
              <button type="submit" class="btn btn-primary" ${this.saving ? 'disabled' : ''}>
                ${this.saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>

          <form class="slug-form" id="slug-form">
            <div class="section-title">Profile URL</div>
            <p class="slug-info">Your unique URL: <a href="${window.location.origin}/u/${this.user.slug}" target="_blank">${window.location.origin}/u/${this.user.slug}</a></p>
            
            <ag-form-group>
              <label for="slug">Update URL Slug</label>
              <input 
                class="form-control" 
                type="text" 
                id="slug" 
                name="slug" 
                value="${this.user.slug}" 
                placeholder="your-custom-slug"
                pattern="[a-z0-9\\-]{3,50}"
                title="3-50 characters, lowercase letters, numbers, and hyphens only"
              />
              <div id="slug-status-container"></div>
              <div>
                <small class="text-muted">3-50 characters, lowercase letters, numbers, and hyphens only</small>
                <br>
                <small class="text-danger">Notice: You can only change your URL once every 12 months.</small>
              </div>
             
              ${this.getSlugUpdateInfo()}
            </ag-form-group>

            <div class="ag-button-group right">
              <button type="submit" class="btn btn-primary" ${this.checkingSlug || this.slugAvailable === false ? 'disabled' : ''}>
                Update URL
              </button>
            </div>
          </form>

          <form class="password-form" id="password-form">
            <h2>Change Password</h2>
            
            <ag-form-group>
              <label for="currentPassword">Current Password</label>
              <input class="form-control" type="password" id="currentPassword" name="currentPassword" required />
            </ag-form-group>

            <ag-form-group>
              <label for="newPassword">New Password</label>
              <input class="form-control" type="password" id="newPassword" name="newPassword" required minlength="6" />
            </ag-form-group>

            <ag-form-group>
              <label for="confirmPassword">Confirm New Password</label>
              <input class="form-control"  type="password" id="confirmPassword" name="confirmPassword" required minlength="6" />
            </ag-form-group>

            <div class="ag-button-group right">
              <button type="submit" class="btn btn-primary" ${this.changingPassword ? 'disabled' : ''}>
                ${this.changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>

          ${this.showSlugConfirmModal ? this.renderSlugConfirmModal() : ''}
        </ag-panel-content>
      </ag-panel>
    `;
  }

  private getSlugUpdateInfo(): string {
    if (!this.user?.lastSlugUpdate) {
      return '';
    }

    const lastUpdate = new Date(this.user.lastSlugUpdate);
    const nextUpdate = new Date(lastUpdate);
    nextUpdate.setFullYear(nextUpdate.getFullYear() + 1);
    const now = new Date();

    if (now < nextUpdate) {
      return `<small class="text-muted">Next update available: ${nextUpdate.toLocaleDateString()}</small>`;
    }

    return '';
  }

  private renderSlugConfirmModal(): string {
    return `
      <div class="modal-overlay" id="slug-modal-overlay">
        <div class="modal-content">
          <h2>Confirm URL Change</h2>
          <p>You are about to change your profile URL to:</p>
          <p class="modal-url"><strong>${window.location.origin}/u/${this.pendingSlug}</strong></p>
          <p class="modal-warning">⚠️ Warning: Your old URL will no longer work. Any links you've shared will break.</p>
          <p class="modal-note">You can only update your URL once per year.</p>
          <div class="ag-button-group right">
            <button class="btn btn-secondary" id="cancel-slug-btn">Cancel</button>
            <button class="btn btn-primary" id="confirm-slug-btn">Confirm Change</button>
          </div>
        </div>
      </div>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.user = session.getUser();
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const profileForm = this.shadowRoot?.querySelector('#profile-form');
    if (profileForm) {
      profileForm.addEventListener('submit', (e) => this.handleSave(e));
    }

    const passwordForm = this.shadowRoot?.querySelector('#password-form');
    if (passwordForm) {
      passwordForm.addEventListener('submit', (e) => this.handlePasswordChange(e));
    }

    const resendBtn = this.shadowRoot?.querySelector('#resend-verification');
    if (resendBtn) {
      resendBtn.addEventListener('click', () => this.handleResendVerification());
    }

    const slugForm = this.shadowRoot?.querySelector('#slug-form');
    if (slugForm) {
      slugForm.addEventListener('submit', (e) => this.handleSlugSubmit(e));
    }

    const slugInput = this.shadowRoot?.querySelector('#slug') as HTMLInputElement;
    if (slugInput) {
      slugInput.addEventListener('input', (e) => this.handleSlugInput(e));
    }

    const cancelSlugBtn = this.shadowRoot?.querySelector('#cancel-slug-btn');
    if (cancelSlugBtn) {
      cancelSlugBtn.addEventListener('click', () => this.handleCancelSlug());
    }

    const confirmSlugBtn = this.shadowRoot?.querySelector('#confirm-slug-btn');
    if (confirmSlugBtn) {
      confirmSlugBtn.addEventListener('click', () => this.handleConfirmSlug());
    }

    const modalOverlay = this.shadowRoot?.querySelector('#slug-modal-overlay');
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          this.handleCancelSlug();
        }
      });
    }
  }

  private async handleSave(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const data: { name?: string; companyName?: string } = {
      name: stripHtml(formData.get('name') as string),
    };

    if (this.user?.accountType === 'company') {
      data.companyName = stripHtml(formData.get('companyName') as string);
    }

    this.saving = true;
    this.render();
    this.attachEventListeners();

    const result = await api.updateProfile(data);
    if (result.success && result.data) {
      session.setUser(result.data);
      this.user = result.data;
      toast.success('Settings saved successfully!');
    } else {
      toast.error('Failed to save settings. Please try again.');
    }

    this.saving = false;
    this.render();
    this.attachEventListeners();
  }

  private async handlePasswordChange(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const currentPassword = formData.get('currentPassword') as string;
    const newPassword = formData.get('newPassword') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    this.changingPassword = true;
    this.render();
    this.attachEventListeners();

    const result = await api.changePassword(currentPassword, newPassword);
    if (result.success) {
      toast.success('Password changed successfully! Please log in again.');
      // Password change clears session, redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } else {
      toast.error(result.error || 'Failed to change password. Please check your current password.');
      this.changingPassword = false;
      this.render();
      this.attachEventListeners();
    }
  }

  private async handleResendVerification(): Promise<void> {
    this.resendingEmail = true;
    this.render();
    this.attachEventListeners();

    const result = await api.resendVerificationEmail();
    if (result.success) {
      toast.success('Verification email sent! Please check your inbox.');
    } else {
      toast.error(result.error || 'Failed to send verification email. Please try again.');
    }

    this.resendingEmail = false;
    this.render();
    this.attachEventListeners();
  }

  private updateSlugStatus(html: string): void {
    const container = this.shadowRoot?.querySelector('#slug-status-container');
    if (container) {
      container.innerHTML = html;
    }
  }

  private updateSubmitButton(disabled: boolean): void {
    const submitBtn = this.shadowRoot?.querySelector('#slug-form button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = disabled;
    }
  }

  private handleSlugInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    const slug = input.value.trim().toLowerCase();

    // Clear previous timeout
    if (this.slugCheckTimeout) {
      clearTimeout(this.slugCheckTimeout);
    }

    // Reset state
    this.slugAvailable = null;

    // Don't check if it's the current slug
    if (slug === this.user?.slug) {
      this.updateSlugStatus('');
      this.updateSubmitButton(false);
      return;
    }

    // Validate format
    if (slug.length < 3 || slug.length > 50) {
      this.updateSlugStatus('<small class="slug-status error">Slug must be 3-50 characters</small>');
      this.updateSubmitButton(true);
      return;
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      this.updateSlugStatus('<small class="slug-status error">Only lowercase letters, numbers, and hyphens allowed</small>');
      this.updateSubmitButton(true);
      return;
    }

    // Debounce the API call
    this.checkingSlug = true;
    this.updateSlugStatus('<small class="slug-status checking">⏳ Checking availability...</small>');
    this.updateSubmitButton(true);

    this.slugCheckTimeout = window.setTimeout(async () => {
      const result = await api.checkSlugAvailability(slug);
      this.checkingSlug = false;

      if (result.success && result.data) {
        this.slugAvailable = result.data.available;
        if (result.data.available) {
          this.updateSlugStatus('<small class="slug-status available">✓ Available</small>');
          this.updateSubmitButton(false);
        } else {
          const reason = result.data.reason || 'Already taken';
          this.updateSlugStatus(`<small class="slug-status taken">✗ ${reason}</small>`);
          this.updateSubmitButton(true);
        }
      } else {
        this.updateSlugStatus('<small class="slug-status error">Failed to check availability</small>');
        this.updateSubmitButton(true);
      }
    }, 300);
  }

  private handleSlugSubmit(e: Event): void {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const newSlug = (formData.get('slug') as string).trim().toLowerCase();

    if (newSlug === this.user?.slug) {
      toast.info('No changes to save');
      return;
    }

    if (this.slugAvailable === false) {
      toast.error('This slug is not available');
      return;
    }

    this.pendingSlug = newSlug;
    this.showSlugConfirmModal = true;
    this.render();
    this.attachEventListeners();
  }

  private handleCancelSlug(): void {
    this.showSlugConfirmModal = false;
    this.pendingSlug = '';
    this.render();
    this.attachEventListeners();
  }

  private async handleConfirmSlug(): Promise<void> {
    this.showSlugConfirmModal = false;
    this.render();
    this.attachEventListeners();

    const result = await api.updateSlug(this.pendingSlug);

    if (result.success && result.data) {
      session.setUser(result.data.user);
      this.user = result.data.user;
      toast.success('Profile URL updated successfully!');
      this.slugAvailable = null;
      this.pendingSlug = '';
    } else {
      toast.error(result.error || 'Failed to update URL. Please try again.');
    }

    this.render();
    this.attachEventListeners();
  }
}

customElements.define('settings-panel', SettingsPanel);
